import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import { MarketExpansionMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionMapDataContext";
import { MarketExpansionLineOfBusinessEnum } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionLineOfBusinessEnum";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { FC, useContext } from "react";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";

const MarketExpansionDashboardFilters: FC = () => {
  const filtersContext = useContext(MarketExpansionFiltersContext);
  const mapDataContext = useContext(MarketExpansionMapDataContext);

  const programItems: DropdownItem<string>[] = filtersContext.programOptions.map(p => ({ key: p.programKey, name: p.program }));
  const selectedProgramItems = programItems.filter(item => filtersContext.selectedPrograms.includes(item.key));
  const noMedicaidProgramsAvailable = filtersContext.selectedLineOfBusiness === MarketExpansionLineOfBusinessEnum.Medicaid
    && filtersContext.programSelectionReady
    && filtersContext.programOptions.length === 0;

  const onCountyCheckboxMouseOver = (dropdownItemKey?: string): void => {
    if (!!dropdownItemKey) {
      mapDataContext.setMousedOverCountySSAs([dropdownItemKey]);
    } else {
      mapDataContext.setMousedOverCountySSAs([]);
    }
  }

  const onCBSAMouseOver = (parentKey?: string): void => {
    if (!!parentKey) {
      const cbsa: NestingParentDropdownItem | undefined = filtersContext.getCBSAGroupedDropdownItems(filtersContext.countyOptions).find(x => x.key === parentKey);
      if (cbsa) {
        mapDataContext.setMousedOverCountySSAs(cbsa?.children.map(x => x.key));
      }
    } else {
      mapDataContext.setMousedOverCountySSAs([]);
    }
  }

  const resetButton = (
    <QESButton
      qesType="secondary-danger"
      qesTestId="market-expansion-dashboard__reset-filters"
      onClick={() => {
        filtersContext.resetFilters();
      }}
    >
      Reset Filters
    </QESButton>
  );

  const renderContent = () => {
    if (filtersContext.clientNotConfiguredError) {
      return (
        <QESAlert type="danger" persist={true}>
          Configuration has not been set up for the New Market Expansion Dashboard. Please contact your client services representative to resolve this issue.
        </QESAlert>
      );
    }

    return (
      <div className="mb-3 row ps-2">
        <QESSelect
          containerClassName="col-12 col-xl-2"
          disabled={filtersContext.linesOfBusinessLoading || !filtersContext.linesOfBusiness.length || filtersContext.linesOfBusiness.length == 1}
          testId="market-expansion-dashboard__line-of-business-filter"
          label="Line of Business"
          onChange={(e) => filtersContext.setSelectedLineOfBusiness(parseInt(e.target.value))}
          value={filtersContext.selectedLineOfBusiness || ""}
        >
          {Object.values(filtersContext.linesOfBusiness).map((lineOfBusiness, index) => (
            <option
              key={index}
              value={lineOfBusiness}
            >
              {MarketExpansionLineOfBusinessEnum[lineOfBusiness]}
            </option>
          ))}
        </QESSelect>

        {filtersContext.stateCountyFilterOptionsLoading ? (
          <div className="col-12 col-xl-4 mt-4" data-testid="market-expansion-dashboard__state-county-loading">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <QESSelect
              containerClassName="col-12 col-xl-2"
              disabled={!filtersContext.selectedLineOfBusiness || filtersContext.linesOfBusinessLoading}
              testId="market-expansion-dashboard__state-filter"
              label="State"
              onChange={(e) => filtersContext.setSelectedState(e.target.value ?? null)}
              value={filtersContext.selectedState || ""}
            >
              {Object.values(filtersContext.stateOptions).map((state) => (
                <option
                  key={state.stateAbbreviation}
                  value={state.stateName}
                >
                  {state.stateName}
                </option>
              ))}
            </QESSelect>

            {filtersContext.selectedLineOfBusiness === MarketExpansionLineOfBusinessEnum.Medicaid && (
              <MultiselectDropdown<string>
                containerClassName="col-12 col-xl-2"
                customSelectInputText={filtersContext.allProgramsSelected ? "All" : undefined}
                descriptiveText={filtersContext.programsError
                  ? "Failed to load programs, please refresh to try again."
                  : noMedicaidProgramsAvailable ? "No data available" : undefined}
                descriptiveTextClassname="text-danger"
                disabled={!filtersContext.selectedState || filtersContext.stateNotFoundError || noMedicaidProgramsAvailable || filtersContext.programsError}
                isLoading={filtersContext.programsLoading}
                items={programItems}
                label="Programs"
                testId="market-expansion-dashboard__program-filter"
                onSelectionChanged={(selectedItems: DropdownItem<string>[]) => {
                  filtersContext.setSelectedPrograms(selectedItems.map(item => item.key));
                }}
                selectedItems={selectedProgramItems}
              />
            )}

            <MultiselectNestedDropdown
              containerClassName="col-12 col-xl-2"
              customSelectInputText={filtersContext.allCountiesSelected ? "All" : undefined}
              disabled={!filtersContext.selectedState || filtersContext.stateNotFoundError}
              hasDuplicates={true}
              items={filtersContext.getCBSAGroupedDropdownItems(filtersContext.countyOptions ?? [])}
              label="Service Area"
              testId="market-expansion-dashboard__county-filter"
              onDropdownItemMouseOver={onCountyCheckboxMouseOver}
              onParentMouseOver={onCBSAMouseOver}
              onSelectionChanged={(selectedItems: string[]) => {
                filtersContext.setSelectedCounties(selectedItems);
              }}
              selectedItems={filtersContext.selectedCounties}
            />
          </>
        )}

        <div className="col-12 col-xl-4 mt-4">
          {resetButton}
        </div>
      </div>
    );
  };

  return (
    <div className="market-expansion-filters">
      {renderContent()}
    </div>
  );
}

export default MarketExpansionDashboardFilters;
